import { motion, AnimatePresence } from "framer-motion";
import { Crown, AlertOctagon, X, Sparkles, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useNavigate } from "react-router-dom";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";

interface ExpiredDownloadDialogProps {
  isOpen: boolean;
  onClose: () => void;
  mediaId?: string; // Optional if we just want to promote VIP
}

export function ExpiredDownloadDialog({ isOpen, onClose, mediaId }: ExpiredDownloadDialogProps) {
  const navigate = useNavigate();

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-md bg-transparent border-none shadow-none p-0 overflow-visible">
        <DialogTitle className="sr-only">Download Expirado</DialogTitle>
        <DialogDescription className="sr-only">Este arquivo não está mais disponível.</DialogDescription>
        
        <motion.div
          initial={{ scale: 0.9, opacity: 0, y: 20 }}
          animate={{ scale: 1, opacity: 1, y: 0 }}
          exit={{ scale: 0.9, opacity: 0, y: 20 }}
          className="relative bg-foreground/5 rounded-3xl overflow-hidden border border-white/10 shadow-[0_0_50px_rgba(255,106,0,0.2)]"
        >
          {/* Animated Glow */}
          <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[200%] h-[200px] bg-gradient-to-b from-[#FF0055]/20 to-transparent blur-[60px] pointer-events-none" />

          {/* Close Button */}
          <button 
            onClick={onClose}
            className="absolute top-4 right-4 z-50 p-2 bg-black/50 hover:bg-white/10 rounded-full transition-colors text-white/50 hover:text-white"
          >
            <X className="w-5 h-5" />
          </button>

          <div className="p-8 flex flex-col items-center text-center relative z-10">
            <div className="w-20 h-20 bg-gradient-to-br from-[#FF0055] to-[#FF6A00] rounded-full flex items-center justify-center mb-6 shadow-[0_0_30px_rgba(255,106,0,0.5)]">
              <AlertOctagon className="w-10 h-10 text-white" />
            </div>

            <h2 className="text-3xl font-black text-white uppercase tracking-tight mb-2">
              DOWNLOAD EXPIRADO
            </h2>
            
            <p className="text-gray-400 mb-6 max-w-sm font-medium">
              Este arquivo não está mais disponível para download direto.
            </p>

            <div className="bg-black/50 border border-[#FF6A00]/30 rounded-2xl p-6 w-full mb-8 relative overflow-hidden group shadow-[inset_0_0_20px_rgba(255,106,0,0.1)]">
              <div className="absolute inset-0 bg-gradient-to-r from-[#FF0055] to-[#FF6A00] opacity-5 group-hover:opacity-10 transition-opacity" />
              <div className="flex justify-center mb-3">
                <Crown className="w-8 h-8 text-[#FF6A00]" />
              </div>
              <p className="text-sm font-bold text-white uppercase tracking-wider leading-relaxed">
                Mas espere! Torne-se um Assinante VIP e desbloqueie TODAS as suas fotos antigas para sempre.
              </p>
            </div>

            <div className="flex flex-col gap-3 w-full">
              <Button
                onClick={() => {
                  onClose();
                  navigate('/subscription');
                }}
                className="w-full h-14 bg-gradient-to-r from-[#FF0055] to-[#FF6A00] hover:from-[#FF0055]/80 hover:to-[#FF6A00]/80 text-white font-black uppercase tracking-widest text-sm rounded-xl flex items-center justify-center gap-3 transition-transform hover:scale-105 shadow-[0_0_20px_rgba(255,106,0,0.4)]"
              >
                ASSINAR VIP AGORA
                <ArrowRight className="w-5 h-5" />
              </Button>
              
              {mediaId && (
                <Button 
                  variant="ghost"
                  onClick={() => {
                    onClose();
                    navigate(`/checkout?media_ids=["${mediaId}"]&is_recovery=true`);
                  }}
                  className="w-full text-white/50 hover:text-white hover:bg-white/5 font-bold uppercase tracking-widest text-xs h-12"
                >
                  PAGAR TAXA DE RECUPERAÇÃO (50%)
                </Button>
              )}
            </div>
          </div>
        </motion.div>
      </DialogContent>
    </Dialog>
  );
}
